import { NumberArray } from '@wonderlandengine/api';
import { CloudData } from '../data/cloud-data.js';
import { ViewStates } from '../components/app.js';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

export enum PaintTypes {
	Solid,
	Metallic,
	Solitallic,
	Satin
}

export enum WheelTypes {
	Superlite,
	Multispoke,
	Aerolite,
	Sportlite
}

// the back-office option ids a bespoke ('select your own') body colour is recorded as, one per paint type that offers it.
// the id carries the description and price; the colour itself is not part of it.
export enum BespokePaintColours {
	Solid = 'BCSF001',
	Metallic = 'BCSF002',
}

export enum CaliperOptions {
	Black = 'BCF001',
	Coral = 'BCF002',
}

export enum GearStickOptions {
	Standard = 'GS001',
	Aluminium = 'GS002',
}

export enum DoorGraphicTypes {
	Black,
	Clear,
	White
}

export enum LeatherTypes {
	TwoTone,
	PebbleGrain,
	SoftGrain
}

export enum SeatBackStyles {
	LeatherWrapped = 'SB002',
	TwoToneTextile = 'NONE'
}

export enum SeatPleatingStyles {
	Horizontal = 'T001',
	Quilted = 'T002',
	Box = 'T003',
	VerticalS400 = 'STD001',
	ExtendedS400 = 'STD002'
}

export enum WoodTypes {
	Standard = 'STANDARD',
	Engineered = 'ENGINEERED'
}

export enum SteeringWheelOptions {
	Silver = 'SWC001',
	Black = 'SWC002',
	BlackAlcantara = 'SW010',
}

export enum SteeringColumnOptions {
	BlackComposite = 'SWC006',
	BlackLeather = 'SWC007',
}

export enum AudioOptions {
	Bluetooth = 'CP004',
	Sennheiser = 'CP010',
}

export enum AccentPackOptions {
	None = 'NONE',
	Dark = 'EA001'
}

export enum TunnelSidesOptions {
	Carpet = 'LO001',
	Leather = 'LO002'
}

export enum EmbroideryOptions {
	None = 'HR004',
	Wings = 'HR001',
	Custom = 'HR003'
}

export enum HardTopOptions {
	None = 'HT001',
	BodyColoured = 'HT003',
	Mohair = 'HT004',
	GreyHardTopS400 = 'HT009',
	GreyHardTopPlusMohairS400 = 'HT010',
}

export enum UpholsteryContrastOptions {
	None = 'NONE',
	BlackAlcantara = 'LP025',
}

interface CostBreakdown {
	model: number;
	paintwork: number;
	wheels: number;
	interior: number;
	cockpit: number;
	exterior: number;
	accessories: number;
}

interface DebugOptions {
	autoStart: boolean;
	autoStartView: ViewStates;
}

//-----------------------------------------------------------------------------------------
// Constants:
//-----------------------------------------------------------------------------------------

const SUPERSPORT_MODEL_ID = '11';															// supersport model ID.
const SUPERSPORT_400_MODEL_ID = '12';														// supersport 400 model ID.
const DEFAULT_MODEL_ID = SUPERSPORT_MODEL_ID;												// model is set by querystring param, but can override default here.

const SEND_TO_DEALER_MOBILE_LEAD_ID = '196';												// send to dealer lead ID for mobile.
const SEND_TO_DEALER_DESKTOP_LEAD_ID = '189';												// send to dealer lead ID for desktop.
const PDF_DOWNLOAD_MOBILE_LEAD_ID = '218';													// pdf download lead ID for mobile.
const PDF_DOWNLOAD_DESKTOP_LEAD_ID = '242';													// pdf download lead ID for desktop

const SEND_TO_DEALER_MOBILE_400_LEAD_ID = '264';											// send to dealer lead ID for mobile (for Supersport 400 model).
const SEND_TO_DEALER_DESKTOP_400_LEAD_ID = '265';											// send to dealer lead ID for desktop (for Supersport 400 model).
const PDF_DOWNLOAD_MOBILE_400_LEAD_ID = '262';												// pdf download lead ID for mobile (for Supersport 400 model).
const PDF_DOWNLOAD_DESKTOP_400_LEAD_ID = '261';												// pdf download lead ID for desktop (for Supersport 400 model).

const STITCHING_MATCHING_LEATHER_STYLE_ID = 'S001';											// stitching matching leather style ID.
const EMBROIDERY_OPTION_MATCHING_LEATHER_STYLE_ID = 'HR004';								// headrest embroidery option matching leather style ID.

const DISCONTINUED_LEATHER_STYLE_IDS: readonly string[] = ['LS007'];						// leather styles no longer offered, but still held by saved configurations.

const DASHBOARD_MATCHING_GLOSS_BODY_COLOUR_ID = 'D001';										// dashboard matching gloss body colour ID.
const DASHBOARD_MATCHING_MATTE_BODY_COLOUR_ID = 'D027';										// dashboard matching matte body colour ID.

const SEAT_PLEATING_OPTION_FOR_TWO_TONE_LEATHER = 'T001';									// seat pleating option for two-tone leather.

const CENTRE_CONSOLE_OPTION_FOR_S400_VENEER = 'CW018';										// centre console option for S400 veneer (for Supersport 400 model).

const HEATED_SEATS_OPTION = 'SF001';														// heated seats option.
const COMFORT_PLUS_OPTION = 'SF004';														// comfort plus option.
const SILVER_SPORTS_EXHAUST_OPTION = 'E009';												// silver sports exhaust option.
const BLACK_SPORTS_EXHAUST_OPTION = 'E010';													// black sports exhaust option.
const CUP_HOLDER_OPTION = 'SF005';															// cup holder option.
const CONNECTIVITY_PACK_OPTION = 'CP012';													// connectivity pack option.
const AIR_CON_OPTION = 'CP001';																// air con option.
const TUNNEL_STORAGE_OPTION = 'SBAG014';													// tunnel storage option.
const PRE_EQUIPMENT_PACK_OPTION = 'AM032';													// pre-equipment pack aftermarket accessory option.
const BLACK_LUGGAGE_RACK_OPTION = 'AM045';													// black luggage rack option.
const CORAL_SILL_GRAPHIC_OPTION = 'GDS002';													// coral sill graphic option.

const SILVER_SPORTS_EXHAUST_OPTION_400 = 'EX007';											// silver sports exhaust option (for Supersport 400 model).
const BLACK_SPORTS_EXHAUST_OPTION_400 = 'EX008';											// black sports exhaust option (for Supersport 400 model).

const DEFAULT_REGION_CODE = 'gb';															// uk.
const DEFAULT_LANGUAGE_CODE = 'en';															// english.
const DEFAULT_PAINT_COLOUR_ID = 'PM025';													// armeria lilac paint colour.
const DEFAULT_WHEEL_STYLE_ID = 'W033';														// 18" superlite silver.
const DEFAULT_LEATHER_STYLE_ID = 'LP018';													// two-tone pebble (black/grey).
const DEFAULT_TUNNEL_SIDES_STYLE_ID = 'LO001';												// carpet tunnel sides.
const DEFAULT_SEAT_BACK_STYLE_ID = SeatBackStyles.TwoToneTextile as string;					// two-tone textile.
const DEFAULT_SEAT_STITCHING_OPTION_ID = 'S001';											// matching upholstery style.
const DEFAULT_CARPET_COLOUR_ID = 'CSW001';													// black standard weave.
const DEFAULT_DASHBOARD_OPTION_ID = 'D024';													// matte silver dashboard face.
const DEFAULT_STEERING_WHEEL_OPTION_ID = 'SWC001';											// silver steering wheel.
const DEFAULT_STEERING_COLUMN_OPTION_ID = SteeringColumnOptions.BlackComposite as string;	// black composite steering column option.
const DEFAULT_HOOD_STYLE_OPTION_ID = 'H001';												// black hood.
const DEFAULT_HARD_TOP_OPTION_ID = HardTopOptions.None;										// no hard top.
const DEFAULT_SEAT_PLEATING_OPTION_ID = SeatPleatingStyles.Horizontal as string;			// horizontal stitch.
const DEFAULT_HEADREST_EMBROIDERY_OPTION_ID = 'HR004';										// no logo on headrest.
const DEFAULT_CENTRE_CONSOLE_OPTION_ID = 'CW012';											// ash olive centre console.
const DEFAULT_ACCENT_PACK_OPTION_ID = 'NONE';												// no accent pack.
const DEFAULT_DOOR_GRAPHIC_OPTION_ID = 'NONE';												// no door graphic.
const DEFAULT_AUDIO_OPTION_ID = 'CP004';													// bluetooth audio.
const DEFAULT_PAINT_PROTECTION_FILM_OPTION = 'NONE';                                    	// no paint protection film.

const DEFAULT_PAINT_COLOUR_400_ID = 'ST02';													// satin horizon (mineral) blue paint colour.
const DEFAULT_WHEEL_STYLE_400_ID = 'W038';													// 19" sportlite silver (for Supersport 400 model).
const DEFAULT_GEAR_STICK_OPTION_400_ID = 'GS001';											// standard gear stick (for Supersport 400 model).
const DEFAULT_FRONT_BRAKE_CALIPER_OPTION_400_ID = 'BCF001';									// standard black front brake caliper (for Supersport 400 model).
const DEFAULT_STRIPE_GRAPHIC_OPTION_400_ID = 'NONE';										// no stripe graphic (for Supersport 400 model).
const DEFAULT_SILL_GRAPHIC_OPTION_400_ID = 'GDS001';										// siver sill graphic (for Supersport 400 model).
const DEFAULT_LEATHER_STYLE_400_ID = 'LP001';												// black pebble grain leather (for Supersport 400 model).
const DEFAULT_SEAT_PLEATING_OPTION_400_ID = SeatPleatingStyles.VerticalS400 as string;		// vertical stitch (for Supersport 400 model).
const DEFAULT_SEAT_BACK_STYLE_400_ID = SeatBackStyles.LeatherWrapped as string;				// leather wrapped (for Supersport 400 model).
const DEFAULT_UPHOLSTERY_CONTRAST_OPTION_400_ID = 'NONE';									// no upholstery contrast option (for Supersport 400 model).

/**
 * Configuration:
 */
export abstract class Configuration {

	//-----------------------------------------------------------------------------------------
	// Fields:
	//-----------------------------------------------------------------------------------------

	private static _costBreakdown: CostBreakdown = {
		model: 0,
		paintwork: 0,
		wheels: 0,
		interior: 0,
		cockpit: 0,
		exterior: 0,
		accessories: 0
	};
	private static _totalCost = 0;
	private static _invalidOptionSelected = false;

	//-----------------------------------------------------------------------------------------
	// Properties:
	//-----------------------------------------------------------------------------------------

	static get costBreakdown() {
		return this._costBreakdown;
	}

	static get totalCost() {
		return this._totalCost;
	}

	static get invalidOptionSelected() {
		return this._invalidOptionSelected;
	}

	static get stitchingMatchingLeatherStyleId() {
		return STITCHING_MATCHING_LEATHER_STYLE_ID;
	}

	static get embroideryOptionMatchingLeatherStyleId() {
		return EMBROIDERY_OPTION_MATCHING_LEATHER_STYLE_ID;
	}

	static get discontinuedLeatherStyleIds() {
		return DISCONTINUED_LEATHER_STYLE_IDS;
	}

	static get dashboardMatchingGlossBodyColourId() {
		return DASHBOARD_MATCHING_GLOSS_BODY_COLOUR_ID;
	}

	static get dashboardMatchingMatteBodyColourId() {
		return DASHBOARD_MATCHING_MATTE_BODY_COLOUR_ID;
	}

	static get seatPleatingOptionForTwoToneLeather() {
		return SEAT_PLEATING_OPTION_FOR_TWO_TONE_LEATHER;
	}

	static get centreConsoleOptionForS400Veneer() {
		return CENTRE_CONSOLE_OPTION_FOR_S400_VENEER;
	}

	static get silverSportsExhaustOption() {
		return this.isSupersport400Model() ? SILVER_SPORTS_EXHAUST_OPTION_400 : SILVER_SPORTS_EXHAUST_OPTION;
	}

	static get blackSportsExhaustOption() {
		return this.isSupersport400Model() ? BLACK_SPORTS_EXHAUST_OPTION_400 : BLACK_SPORTS_EXHAUST_OPTION;
	}

	static get cupHolderOption() {
		return CUP_HOLDER_OPTION;
	}

	static get connectivityPackOption() {
		return CONNECTIVITY_PACK_OPTION;
	}

	static get airConOption() {
		return AIR_CON_OPTION;
	}

	static get tunnelStorageOption() {
		return TUNNEL_STORAGE_OPTION;
	}

	static get preEquipmentPackOption() {
		return PRE_EQUIPMENT_PACK_OPTION;
	}

	static get blackLuggageRackOption() {
		return BLACK_LUGGAGE_RACK_OPTION;
	}

	static get heatedSeatsOption() {
		return HEATED_SEATS_OPTION;
	}

	static get comfortPlusOption() {
		return COMFORT_PLUS_OPTION;
	}

	static get defaultHoodOption() {
		return DEFAULT_HOOD_STYLE_OPTION_ID;
	}

	static get coralSillGraphicOption() {
		return CORAL_SILL_GRAPHIC_OPTION;
	}

	static get sendToDealerLeadId() {
		if (this.isSupersport400Model()) {
			return this.platformIsMobile ? SEND_TO_DEALER_MOBILE_400_LEAD_ID : SEND_TO_DEALER_DESKTOP_400_LEAD_ID;
		}
		return this.platformIsMobile ? SEND_TO_DEALER_MOBILE_LEAD_ID : SEND_TO_DEALER_DESKTOP_LEAD_ID;
	}

	static get pdfDownloadLeadId() {
		if (this.isSupersport400Model()) {
			return this.platformIsMobile ? PDF_DOWNLOAD_MOBILE_400_LEAD_ID : PDF_DOWNLOAD_DESKTOP_400_LEAD_ID;
		}
		return this.platformIsMobile ? PDF_DOWNLOAD_MOBILE_LEAD_ID : PDF_DOWNLOAD_DESKTOP_LEAD_ID;
	}

	// the page title used when running locally - the published sites carry their own, injected into index.html at publish time.
	static get pageTitle() {
		return this.isSupersport400Model() ? 'Morgan Supersport 400' : 'Morgan Supersport';
	}

	//-----------------------------------------------------------------------------------------
	// Public Static Fields: (equivalent to read/write Properties in this context)
	//-----------------------------------------------------------------------------------------

	// ensure debug options are turned off before going live!!
	static debugOptions: DebugOptions = {
		autoStart: false,
		autoStartView: ViewStates.Accessories
	};

	static platformIsMobile = false; // set by scene controller on start.

	static modelId = DEFAULT_MODEL_ID;

	static regionCode = DEFAULT_REGION_CODE;
	static languageCode = DEFAULT_LANGUAGE_CODE;

	static bodyPaintColour = Configuration.getDefaultPaintStyle();

	// the rgba of a bespoke body colour, held alongside the bespoke option id in 'bodyPaintColour' - the id carries the description
	// and price, this carries the colour itself. null whenever the selected paint is a standard one.
	static bespokePaintColour: NumberArray | null = null;

	// whether the special paint information has been shown on the customer's first selection of a bespoke colour. it is held here
	// because the paintwork view is unmounted on every change of step, and it is deliberately not cleared by a reset - the customer
	// has read it once already, so it is not shown again for the rest of the session.
	static hasShownSpecialPaintInformation = false;

	static wheelStyle = Configuration.getDefaultWheelStyle();
	static leatherStyle = Configuration.getDefaultLeatherStyle();
	static tunnelSidesStyle = DEFAULT_TUNNEL_SIDES_STYLE_ID;
	static seatBackStyle = Configuration.getDefaultSeatBackStyle();
	static seatStitchingOption = DEFAULT_SEAT_STITCHING_OPTION_ID;
	static carpetColour = DEFAULT_CARPET_COLOUR_ID;
	static dashboardOption = DEFAULT_DASHBOARD_OPTION_ID;
	static steeringWheelOption = DEFAULT_STEERING_WHEEL_OPTION_ID;
	static steeringColumnOption = DEFAULT_STEERING_COLUMN_OPTION_ID;
	static hoodStyleOption = DEFAULT_HOOD_STYLE_OPTION_ID;
	static hardTopOption = DEFAULT_HARD_TOP_OPTION_ID;
	static seatPleatingOption = Configuration.getDefaultSeatPleatingOption();
	static headrestEmbroideryOption = DEFAULT_HEADREST_EMBROIDERY_OPTION_ID;
	static centreConsoleOption = DEFAULT_CENTRE_CONSOLE_OPTION_ID;
	static accentPackOption = DEFAULT_ACCENT_PACK_OPTION_ID;
	static doorGraphicOption = DEFAULT_DOOR_GRAPHIC_OPTION_ID;
	static audioOption = DEFAULT_AUDIO_OPTION_ID;
	static paintProtectionFilmOption = DEFAULT_PAINT_PROTECTION_FILM_OPTION;

	static accessoryStorageOptions = new Set<string>();
	static accessoryCoversOptions = new Set<string>();
	static accessoryEquipmentOptions = new Set<string>();
	static accessoryServicesOptions = new Set<string>();
	static accessoryAftermarketOptions = new Set<string>();

	static gearStickOption = DEFAULT_GEAR_STICK_OPTION_400_ID;
	static frontBrakeCaliperOption = DEFAULT_FRONT_BRAKE_CALIPER_OPTION_400_ID;
	static stripeGraphicOption = DEFAULT_STRIPE_GRAPHIC_OPTION_400_ID;
	static sillGraphicOption = DEFAULT_SILL_GRAPHIC_OPTION_400_ID;
	static upholsteryContrastOption = DEFAULT_UPHOLSTERY_CONTRAST_OPTION_400_ID;

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------

	static resetConfiguration() {
		this.bodyPaintColour = this.getDefaultPaintStyle();
		this.bespokePaintColour = null;
		this.wheelStyle = this.getDefaultWheelStyle();
		this.leatherStyle = this.getDefaultLeatherStyle();
		this.tunnelSidesStyle = DEFAULT_TUNNEL_SIDES_STYLE_ID;
		this.seatBackStyle = this.getDefaultSeatBackStyle();
		this.seatStitchingOption = DEFAULT_SEAT_STITCHING_OPTION_ID;
		this.carpetColour = DEFAULT_CARPET_COLOUR_ID;
		this.dashboardOption = DEFAULT_DASHBOARD_OPTION_ID;
		this.steeringWheelOption = DEFAULT_STEERING_WHEEL_OPTION_ID;
		this.steeringColumnOption = DEFAULT_STEERING_COLUMN_OPTION_ID;
		this.hoodStyleOption = DEFAULT_HOOD_STYLE_OPTION_ID;
		this.hardTopOption = DEFAULT_HARD_TOP_OPTION_ID;
		this.seatPleatingOption = this.getDefaultSeatPleatingOption();
		this.headrestEmbroideryOption = DEFAULT_HEADREST_EMBROIDERY_OPTION_ID;
		this.centreConsoleOption = DEFAULT_CENTRE_CONSOLE_OPTION_ID;
		this.accentPackOption = DEFAULT_ACCENT_PACK_OPTION_ID;
		this.doorGraphicOption = DEFAULT_DOOR_GRAPHIC_OPTION_ID;
		this.audioOption = DEFAULT_AUDIO_OPTION_ID;
		this.paintProtectionFilmOption = DEFAULT_PAINT_PROTECTION_FILM_OPTION;

		this.accessoryStorageOptions = new Set<string>();
		this.accessoryCoversOptions = new Set<string>();
		this.accessoryEquipmentOptions = new Set<string>();
		this.accessoryServicesOptions = new Set<string>();
		this.accessoryAftermarketOptions = new Set<string>();

		this.gearStickOption = DEFAULT_GEAR_STICK_OPTION_400_ID;
		this.frontBrakeCaliperOption = DEFAULT_FRONT_BRAKE_CALIPER_OPTION_400_ID;
		this.stripeGraphicOption = DEFAULT_STRIPE_GRAPHIC_OPTION_400_ID;
		this.sillGraphicOption = DEFAULT_SILL_GRAPHIC_OPTION_400_ID;
		this.upholsteryContrastOption = DEFAULT_UPHOLSTERY_CONTRAST_OPTION_400_ID;
	}

	static isUsRegion(): boolean {
		return this.regionCode === 'us';
	}

	static isSupersport400Model(): boolean {
		return this.modelId === SUPERSPORT_400_MODEL_ID;
	}

	static isSupersportModelInUsRegion(): boolean {
		return this.modelId === SUPERSPORT_MODEL_ID && this.isUsRegion();
	}

	static isSupersport400ModelInUsRegion(): boolean {
		return this.isSupersport400Model() && this.isUsRegion();
	}

	static isBespokePaintColour(id: string): boolean {
		return (Object.values(BespokePaintColours) as string[]).indexOf(id) !== -1;
	}

	static getDefaultPaintStyle(): string {
		return this.isSupersport400Model() ? DEFAULT_PAINT_COLOUR_400_ID : DEFAULT_PAINT_COLOUR_ID;
	}

	static getDefaultWheelStyle(): string {
		return this.isSupersport400Model() ? DEFAULT_WHEEL_STYLE_400_ID : DEFAULT_WHEEL_STYLE_ID;
	}

	static getDefaultSeatPleatingOption(): string {
		return this.isSupersport400Model() ? DEFAULT_SEAT_PLEATING_OPTION_400_ID : DEFAULT_SEAT_PLEATING_OPTION_ID;
	}

	static getDefaultLeatherStyle(): string {
		return this.isSupersport400Model() ? DEFAULT_LEATHER_STYLE_400_ID : DEFAULT_LEATHER_STYLE_ID;
	}

	static getDefaultSeatBackStyle(): string {
		return this.isSupersport400Model() ? DEFAULT_SEAT_BACK_STYLE_400_ID : DEFAULT_SEAT_BACK_STYLE_ID;
	}
	
	static isHardTopOnly(hardTopOption: HardTopOptions): boolean {
		return hardTopOption === HardTopOptions.BodyColoured || hardTopOption === HardTopOptions.GreyHardTopS400;
	}

	static isHardTopAndSoftTop(hardTopOption: HardTopOptions): boolean {
		return hardTopOption === HardTopOptions.Mohair || hardTopOption === HardTopOptions.GreyHardTopPlusMohairS400;
	}

	static isGreyHardTop(hardTopOption: HardTopOptions): boolean {
		return hardTopOption === HardTopOptions.GreyHardTopS400 || hardTopOption === HardTopOptions.GreyHardTopPlusMohairS400;
	}

	// toggles the given accessory option, adding or removing the given ID from the list, and updating the costs.
	static toggleAccessoryOption(accessoryOptions: Set<string>, id: string) {
		if (accessoryOptions.has(id)) {
			accessoryOptions.delete(id);
		} 
		else {
			accessoryOptions.add(id);
		}
		this.updateCosts();
	}

	// adds the given ID to the given accessory options, updating the costs.
	static addAccessoryOption(accessoryOptions: Set<string>, id: string) {
		accessoryOptions.add(id);
		this.updateCosts();
	}

	// removes the given ID from the given accessory options, updating the costs.
	static removeAccessoryOption(accessoryOptions: Set<string>, id: string) {
		if (accessoryOptions.has(id)) {
			accessoryOptions.delete(id);
		}
		this.updateCosts();
	}

	// returns the relevant logo properties to use in an img tag, based on the model.
	// usage: <img {...Configuration.logoProps()} />
	static logoProps() {
		return this.isSupersport400Model() ? {
			src: 'assets/images/logo/S400-logo.png',
			width: 'auto',
			height: 30,
			alt: 'Supersport 400'
		} : {
			src: 'assets/images/logo/supersport-logo.svg',
			width: 280,
			height: 'auto',
			alt: 'Supersport'
		};
	}

	// updates the cost fields (cost breakdown, total cost and invalid option selected flag).
	static updateCosts() {

		// this will be set to true by the "getPrice" function calls if any of the prices returned are invalid.
		const isInvalidOptionRef = {
			ref: false
		}

		// model base price.
		const modelCost = CloudData.getPrice(this.modelId, CloudData.modelOptions, isInvalidOptionRef);
		this._costBreakdown.model = modelCost;

		// paintwork.
		const bodyPaintCost = CloudData.getPrice(this.bodyPaintColour, CloudData.paintworkColourOptions, isInvalidOptionRef);
		this._costBreakdown.paintwork = bodyPaintCost;

		// wheels.
		const wheelStyle = this.wheelStyle;
		const wheelStyleCost = CloudData.getPrice(wheelStyle, CloudData.wheelStyleOptions, isInvalidOptionRef);
		this._costBreakdown.wheels = wheelStyleCost;
		if (this.isSupersport400Model()) {
			const frontBrakeCaliperOptionCost = CloudData.getPrice(this.frontBrakeCaliperOption, CloudData.accessoryOptions, isInvalidOptionRef);
			this._costBreakdown.wheels += frontBrakeCaliperOptionCost;
		}

		// interior.
		const leatherStyleCost = CloudData.getPrice(this.leatherStyle, CloudData.leatherStyleOptions, isInvalidOptionRef);
		const tunnelSideStyleCost = CloudData.getPrice(this.tunnelSidesStyle, CloudData.leatherStyleOptions, isInvalidOptionRef);
		const seatPleatingOptionCost = CloudData.getPrice(this.seatPleatingOption, CloudData.seatPleatingOptions, isInvalidOptionRef);
		const seatBackStyleCost = CloudData.getPrice(this.seatBackStyle, CloudData.seatBackStyleOptions, isInvalidOptionRef);
		const headsetEmbroideryCost = CloudData.getPrice(this.headrestEmbroideryOption, CloudData.headrestEmbroideryOptions, isInvalidOptionRef);
		const seatStitchingCost = CloudData.getPrice(this.seatStitchingOption, CloudData.seatStitchingOptions, isInvalidOptionRef);
		const carpetColorCost = CloudData.getPrice(this.carpetColour, CloudData.carpetColourOptions, isInvalidOptionRef);
		this._costBreakdown.interior = leatherStyleCost + tunnelSideStyleCost + seatPleatingOptionCost + seatBackStyleCost +
			headsetEmbroideryCost + seatStitchingCost + carpetColorCost;
		if (this.isSupersport400Model()) {
			const upholsteryContrastOptionCost = CloudData.getPrice(this.upholsteryContrastOption, CloudData.leatherStyleOptions, isInvalidOptionRef);
			this._costBreakdown.interior += upholsteryContrastOptionCost;
		}

		// cockpit:
		const dashboardOptionCost = CloudData.getPrice(this.dashboardOption, CloudData.dashboardOptions, isInvalidOptionRef);
		const steeringWheelOptionCost = CloudData.getPrice(this.steeringWheelOption, CloudData.steeringWheelOptions, isInvalidOptionRef);
		const steeringColumnOptionCost = CloudData.getPrice(this.steeringColumnOption, CloudData.steeringWheelOptions, isInvalidOptionRef);
		const centreConsoleOptionCost = CloudData.getPrice(this.centreConsoleOption, CloudData.centreConsoleOptions, isInvalidOptionRef);
		this._costBreakdown.cockpit = dashboardOptionCost + steeringWheelOptionCost + steeringColumnOptionCost + centreConsoleOptionCost;
		if (this.isSupersport400Model()) {
			const gearStickOptionCost = CloudData.getPrice(this.gearStickOption, CloudData.accessoryOptions, isInvalidOptionRef);
			this._costBreakdown.cockpit += gearStickOptionCost;
		}

		// exterior:
		const hoodStyleOptionCost = CloudData.getPrice(this.hoodStyleOption, CloudData.hoodStyleOptions, isInvalidOptionRef);
		const hardTopOptionCost = CloudData.getPrice(this.hardTopOption, CloudData.hardTopOptions, isInvalidOptionRef);
		const doorGraphicOptionCost = CloudData.getPrice(this.doorGraphicOption, CloudData.graphicsOptions, isInvalidOptionRef);
		const accentPackOptionCost = CloudData.getPrice(this.accentPackOption, CloudData.accessoryOptions, isInvalidOptionRef);
		const paintProtectionFilmOption = CloudData.getPrice(this.paintProtectionFilmOption, CloudData.accessoryOptions, isInvalidOptionRef);
		this._costBreakdown.exterior = hoodStyleOptionCost + hardTopOptionCost + doorGraphicOptionCost + accentPackOptionCost + paintProtectionFilmOption;
		if (this.isSupersport400Model()) {
			const stripeGraphicOptionCost = CloudData.getPrice(this.stripeGraphicOption, CloudData.graphicsOptions, isInvalidOptionRef);
			const sillGraphicOptionCost = CloudData.getPrice(this.sillGraphicOption, CloudData.graphicsOptions, isInvalidOptionRef);
			this._costBreakdown.exterior += stripeGraphicOptionCost + sillGraphicOptionCost;
		}

		// accessories:
		const accessoryStorageOptionsCost = CloudData.getAccessoryPrices(this.accessoryStorageOptions, isInvalidOptionRef);
		const accessoryCoversOptionsCost = CloudData.getAccessoryPrices(this.accessoryCoversOptions, isInvalidOptionRef);
		const accessoryEquipmentOptionsCost = CloudData.getAccessoryPrices(this.accessoryEquipmentOptions, isInvalidOptionRef);
		const accessoryServicesOptionsCost = CloudData.getAccessoryPrices(this.accessoryServicesOptions, isInvalidOptionRef);
		const accessoryAftermarketOptionsCost = CloudData.getAccessoryPrices(this.accessoryAftermarketOptions, isInvalidOptionRef);
		const accessoryAudioOptionCost = CloudData.getPrice(this.audioOption, CloudData.accessoryOptions, isInvalidOptionRef);
		this._costBreakdown.accessories = accessoryStorageOptionsCost + accessoryCoversOptionsCost + accessoryEquipmentOptionsCost +
			accessoryServicesOptionsCost + accessoryAftermarketOptionsCost + accessoryAudioOptionCost;

		// update total cost from the breakdown.
		this._totalCost =
			this._costBreakdown.model +
			this._costBreakdown.paintwork +
			this._costBreakdown.wheels +
			this._costBreakdown.interior +
			this._costBreakdown.cockpit +
			this._costBreakdown.exterior +
			this._costBreakdown.accessories;

		// update the flag.
		this._invalidOptionSelected = isInvalidOptionRef.ref;
	}
}
